import React from 'react';
import { 
  Globe, 
  MapPin, 
  Calendar, 
  FileText, 
  Search, 
  Building, 
  Users, 
  TrendingUp,
  Brain,
  BarChart3,
  Target,
  Lightbulb,
  Book,
  Newspaper,
  University,
  Factory,
  Briefcase
} from 'lucide-react';

// Research type icon mapping
export const getResearchTypeIcon = (researchType: string) => {
  const iconProps = { size: 16, className: "text-blue-500 dark:text-blue-400" };
  
  switch (researchType?.toLowerCase()) {
    case 'market analysis':
      return <BarChart3 {...iconProps} />;
    case 'competitor analysis':
      return <Target {...iconProps} />;
    case 'industry research':
      return <Factory {...iconProps} />;
    case 'financial analysis':
      return <TrendingUp {...iconProps} />;
    case 'academic research':
      return <University {...iconProps} />;
    case 'news research':
      return <Newspaper {...iconProps} />;
    case 'company research':
      return <Building {...iconProps} />;
    case 'general research':
      return <Brain {...iconProps} />;
    case 'product research':
      return <Lightbulb {...iconProps} />;
    case 'user research':
      return <Users {...iconProps} />;
    case 'comprehensive analysis':
      return <Brain {...iconProps} />;
    default:
      return <FileText {...iconProps} />;
  }
};

// Parameter icon mapping
export const getParameterIcon = (key: string, parameters: Record<string, any>) => {
  const iconProps = { size: 14, className: "text-muted-foreground flex-shrink-0" };
  
  switch (key.toLowerCase()) {
    case 'location':
    case 'country':
    case 'region':
      return <MapPin {...iconProps} />;
    case 'timeframe':
    case 'date_range_start':
    case 'date_range_end':
      return <Calendar {...iconProps} />;
    case 'source_type':
    case 'sources':
      return <Book {...iconProps} />;
    case 'type_specific':
      return getResearchTypeIcon(parameters.research_type || 'general');
    case 'industry':
      return <Factory {...iconProps} />;
    case 'company':
      return <Building {...iconProps} />;
    default:
      return <Search {...iconProps} />;
  }
};

// Format timeframe display with color coding
export const formatTimeframeDisplay = (parameters: Record<string, any>): string => {
  if (parameters.timeframe_label) {
    return parameters.timeframe_label;
  }
  
  if (parameters.timeframe) {
    return parameters.timeframe;
  }
  
  if (parameters.date_range_start && parameters.date_range_end) {
    const startDate = new Date(parameters.date_range_start).toLocaleDateString();
    const endDate = new Date(parameters.date_range_end).toLocaleDateString();
    return `${startDate} - ${endDate}`;
  }
  
  if (parameters.date_range_start) {
    const startDate = new Date(parameters.date_range_start).toLocaleDateString();
    return `Since ${startDate}`;
  }
  
  return 'Custom timeframe';
};

// Parse type-specific parameters
export const parseTypeSpecificParameters = (typeSpecificFilters: Record<string, any>) => {
  const result: Record<string, Array<{ name: string; value: string }>> = {};
  
  if (!typeSpecificFilters || typeof typeSpecificFilters !== 'object') {
    return result;
  }
  
  Object.entries(typeSpecificFilters).forEach(([researchType, filters]) => {
    if (filters && typeof filters === 'object') {
      const params: Array<{ name: string; value: string }> = [];
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.push({
            name: formatKey(key),
            value: String(value)
          });
        }
      });
      
      if (params.length > 0) {
        result[researchType] = params;
      }
    }
  });
  
  return result;
};

// Filter parameters into categories
export const filterParameters = (parameters: Record<string, any>) => {
  const locationParams: Array<[string, any]> = [];
  const sourceParams: Array<[string, any]> = [];
  const mainParams: Array<[string, any]> = [];
  
  const excludedKeys = new Set([
    'research_type', 
    'timeframe', 
    'timeframe_label',
    'date_range_start', 
    'date_range_end',
    'type_specific_filters'
  ]);
  
  Object.entries(parameters).forEach(([key, value]) => {
    if (excludedKeys.has(key) || value === null || value === undefined || value === '') {
      return;
    }
    
    if (key.includes('location') || key.includes('country') || key.includes('region')) {
      locationParams.push([key, value]);
    } else if (key.includes('source')) {
      sourceParams.push([key, value]);
    } else {
      mainParams.push([key, value]);
    }
  });
  
  return { locationParams, sourceParams, mainParams };
};

// Format parameter keys for display
export const formatKey = (key: string): string => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format parameter values for display
export const formatValue = (value: any): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  
  return String(value);
};

// Capitalize first letter utility
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}; 